// src/data/eventsdata.js
import React from 'react';
import { 
  FaCalendarAlt, 
  FaUserGraduate, 
  FaUsers, 
  FaChalkboardTeacher,
  FaBuilding,
  FaGlobe,
  FaTree,
  FaHandsHelping,
  FaComments,
  FaCertificate,
  FaMapMarkedAlt,
  FaProjectDiagram,
  FaPrint,
  FaIndustry,
  FaGraduationCap
} from 'react-icons/fa';

const EVENTS_DATA = {
  // Featured upcoming event
  featured: {
    id: "3d-printing-bootcamp",
    title: "Additive Manufacturing (3D Printing) BootCamp",
    description: "Equip youths and innovators with the skills and design thinking principles to address complex development challenges using a blend of Computer Aided Design and 3D printing technologies.",
    longDescription: `Leotech is implementing the Additive Manufacturing Bootcamp, in collaboration with Rasmus Emil Tofthøj, a freelance design engineer in Denmark, will immerse participants in design thinking methodology for problem-solving, Computer-Aided Design for digital fabrication, and the essentials of 3D printing technology.

The tech and innovation ecosystem is blooming in Sierra Leone and the subregion, with many innovators eager to drive meaningful change through local problem-solving. Government frameworks, academia, the private sector, and multinational institutions are implementing policies, taking action, and committing resources to support local thinkers and problem solvers. However, access to lab equipment and other necessary resources to support local innovators at the grassroots level remains a significant challenge.

This project is a pilot step towards addressing this issue, as we aim to bridge the gap in accessing innovation resources. This initiative will contribute to a larger effort to establish innovation facilities in hard-to-reach communities in rural Sierra Leone and the subregion.

Rasmus will bring his extensive innovation experience from the Technical University of Denmark's Sky Lab, merging it with Leotech's skilled team of volunteers and engineers, alongside our strong track record in youth capacity building in STEM, to achieve the project's objectives.

By the end of the project, participants will have learned:
- The concept of Design Thinking Methodology in problem-solving
- The fundamentals of Additive Manufacturing (AM) as an Industry 4.0 technology
- The importance of collaboration and team-building

Beyond the product development process, we will introduce the business aspects of AM. AM can also be employed to produce hard-to-find spare parts, support small-scale production, and create customised products. A physical prototype serves as a powerful communication tool to attract investors and raise global awareness. A 3D printer cannot solve problems on its own—but in the hands of local designers and makers, it becomes a tool for real change.`,
    date: "2025-07-07T09:00:00",
    endDate: "2025-07-11T17:00:00",
    location: "Freetown Polytechnic",
    category: "workshop",
    image: "/images/events/3d-printing-bootcamp.jpg",
    icon: <FaPrint />,
    attendees: 40,
    featured: true,
    targetGroup: "Youths and innovators",
    registration: {
      required: true,
      url: "/events/register/3d-printing-bootcamp",
      deadline: "2025-06-25T23:59:59"
    },
    speakers: [
      {
        name: "Rasmus Emil Tofthøj",
        title: "Freelance Design Engineer, Denmark",
        linkedin: "https://www.linkedin.com/in/rasmustofthoej"
      }
    ],
    schedule: [
      {
        day: "Day 1",
        title: "Introduction to Design Thinking",
        activities: [
          "Welcome and Introduction",
          "Design Thinking Framework Overview",
          "Problem Identification Exercise",
          "Team Formation"
        ]
      },
      {
        day: "Day 2",
        title: "Computer-Aided Design Fundamentals",
        activities: [
          "Introduction to CAD Software",
          "Basic Modeling Techniques",
          "Design Constraints and Considerations",
          "Project Planning"
        ]
      },
      {
        day: "Day 3",
        title: "3D Printing Technology",
        activities: [
          "Types of 3D Printing Methods",
          "Materials and Applications",
          "Design for Additive Manufacturing",
          "File Preparation for Printing"
        ]
      },
      {
        day: "Day 4",
        title: "Project Development",
        activities: [
          "Guided Project Work",
          "Troubleshooting and Iteration",
          "Print Testing",
          "Prototype Refinement"
        ]
      },
      {
        day: "Day 5",
        title: "Project Presentation and Business Applications",
        activities: [
          "Project Finalization",
          "Business Applications of AM",
          "Presentation Skills Workshop",
          "Final Presentations and Certification"
        ]
      }
    ]
  },
  
  // Upcoming events (2025)
  upcoming: [
    {
      id: "elmo-project-2024",
      title: "ELMO Project Implementation",
      description: "Enhancing the Learning Model and Opportunities for Tertiary Engineering Education in Sierra Leone (ELMO) Project.",
      date: "2024-12-15T09:00:00",
      location: "Kenema, Sierra Leone",
      category: "project",
      image: "/images/events/elmo.png",
      icon: <FaProjectDiagram />,
      attendees: 100,
      featured: false,
      registration: {
        required: true,
        url: "/events/register/elmo-project"
      },
      partners: [
        "Engineers for Change Sierra Leone",
        "Fourah Bay College",
        "Royal Academy of Engineering – UK"
      ],
      details: "Leotech is collaborating with Engineers for Change Sierra Leone and Fourah Bay College in implementing the ELMO project in partnership with local industries, funded by the Royal Academy of Engineering – UK."
    }
  ],
  
  // Past events
  past: [
    {
      id: "cohort-12-orientation-2024",
      title: "Cohort 12 Orientation and Career Talk",
      description: "Orientation session and career talk for the 12th cohort of Leotech trainees.",
      date: "2024-10-15T10:00:00",
      location: "Kingtom Police Wives Centre, Freetown",
      category: "orientation",
      image: "/images/events/cohort-12-orientation.jpg",
      icon: <FaGraduationCap />,
      attendees: 60,
      videoUrl: "https://youtu.be/JqDd1Y1Wfgo",
      past: true
    },
    {
      id: "ewb-partners-summit-2024",
      title: "Engineers Without Borders Partners' Summit",
      description: "Bailor Bah, Director of Programs at Leotech, presents our Strategy at the Engineers Without Borders Partners' Summit.",
      date: "2024-11-10T09:00:00",
      location: "Family Kingdom, Freetown",
      category: "conference",
      image: "/images/events/ewb-partners-summit.jpg",
      icon: <FaGlobe />,
      attendees: 120,
      past: true
    },
    {
      id: "climate-conference-2024",
      title: "Climate Conference with EWB Denmark",
      description: "Leotech leadership joins Engineers Without Borders Denmark representatives at the Climate Conference.",
      date: "2024-05-15T14:00:00",
      location: "Freetown City Council Hall",
      category: "conference",
      image: "/images/events/climate-conference.jpg",
      icon: <FaTree />,
      attendees: 90,
      past: true,
      speakers: [
        {
          name: "Alfred Mbayoh",
          title: "CEO, Leotech SL"
        },
        {
          name: "Dennishia Godwin",
          title: "Manager, Leotech SL"
        },
        {
          name: "Peter Horne Zartsadahl",
          title: "Secretary General, Engineers Without Borders - Denmark"
        },
        {
          name: "Sofie Viborg",
          title: "Project Design Specialist, Engineers Without Borders - Denmark"
        }
      ]
    },
    {
      id: "beach-cleaning-2024",
      title: "Aberdeen Beach Cleaning Exercise",
      description: "Leotech staff collaborated with Engineers without Borders - Denmark, Skills Pool and other organisations in undertaking a beach cleaning exercise.",
      date: "2024-11-05T09:00:00",
      location: "Aberdeen Beach, Freetown",
      category: "community-service",
      image: "/images/events/beach-cleaning.jpg",
      icon: <FaHandsHelping />,
      attendees: 50,
      past: true,
      partners: [
        "Engineers without Borders - Denmark",
        "Skills Pool",
        "Community Volunteers"
      ]
    },
    {
      id: "annual-meeting-2024",
      title: "Annual General Meeting 2024",
      description: "Leotech hosts an annual meeting to take stock of accomplishments and strategize for the following year.",
      date: "2024-12-18T15:00:00",
      location: "Aberdeen, Freetown",
      category: "meeting",
      image: "/images/events/annual-meeting-2024.jpg",
      icon: <FaUsers />,
      attendees: 30,
      past: true
    },
    {
      id: "cohort-8-conference-2023",
      title: "Youth Conference & Cohort 8 Certification",
      description: "A Youth-Centred Approach Towards Unleashing Entrepreneurship Value from Digital Skills.",
      date: "2023-10-25T14:00:00",
      location: "Mining Engineering Auditorium, Fourah Bay College",
      category: "certification",
      image: "/images/events/cohort-8-conference.png",
      icon: <FaCertificate />,
      attendees: 150,
      videoUrl: "https://youtu.be/cSUyKyTCsWI",
      past: true,
      theme: "A Youth-Centred Approach Towards Unleashing Entrepreneurship Value from Digital Skills"
    },
    {
      id: "cohort-6-7-cert-2023",
      title: "Cohort 6 & 7 Certification & Conference",
      description: "Conference and certification ceremony exploring the role of job skills in building a better engineering workforce.",
      date: "2023-06-20T14:00:00",
      location: "Mining Engineering Auditorium, Fourah Bay College",
      category: "certification",
      image: "/images/events/cohort-6-7-cert.jpg",
      icon: <FaCertificate />,
      attendees: 160,
      videoUrl: "https://youtu.be/s4-UYMMwG1U",
      past: true,
      speakers: [
        {
          name: "Ing. Trudy Morgan",
          title: "President, Sierra Leone Institution of Engineers"
        }
      ],
      theme: "The Role of Job Skills in Building a Better Workforce in Engineering"
    },
    {
      id: "entrepreneurship-lecture-2023",
      title: "Public Lecture on Innovation & Entrepreneurship",
      description: "Leotech collaborated with Engineers Without Borders—Denmark to deliver a public lecture on Innovation and Entrepreneurship for Sustainable Development.",
      date: "2023-02-15T14:00:00",
      location: "Tunde Cole Hall, Fourah Bay College",
      category: "lecture",
      image: "/images/events/entrepreneurship-lecture.jpg",
      icon: <FaChalkboardTeacher />,
      attendees: 120,
      past: true,
      theme: "Innovation and Entrepreneurship for Sustainable Development"
    },
    {
      id: "digital-skills-lecture-2023",
      title: "Digital Skills in Engineering Lecture",
      description: "Leotech Collaborates with the Sierra Leone Women Engineers (SLWE) and the Engineering Society to engage engineering students on the relevance of Digital Skills in Engineering.",
      date: "2023-01-25T14:00:00",
      location: "Fourah Bay College",
      category: "lecture",
      image: "/images/events/digital-skills-lecture.jpg",
      icon: <FaChalkboardTeacher />,
      attendees: 100,
      past: true,
      partners: [
        "Sierra Leone Women Engineers (SLWE)",
        "Engineering Society, Fourah Bay College"
      ]
    },
    {
      id: "panel-discussion-2023",
      title: "Panel Discussion on Engineering Skills",
      description: "Panel Discussion featuring engineering experts moderated by Ing. Deborah Momoh during the cohort-6 certification ceremony.",
      date: "2023-06-20T15:30:00",
      location: "Mining Engineering Auditorium, Fourah Bay College",
      category: "panel-discussion",
      image: "/images/events/panel-discussion.jpg",
      icon: <FaComments />,
      attendees: 160,
      past: true,
      speakers: [
        {
          name: "Ing. Rakes Sesay",
          title: "Engineering Expert"
        },
        {
          name: "Ing. Bashiru Bunduka",
          title: "Engineering Expert"
        },
        {
          name: "Ing. Deborah Momoh",
          title: "Moderator"
        }
      ]
    },
    {
      id: "jica-field-trip-2023",
      title: "Field Trip to JICA Construction Site",
      description: "General Field Trip to the Japanese International Cooperation Agency's construction site of the Children Hospital at Lumley.",
      date: "2023-05-18T09:00:00",
      location: "Lumley, Freetown",
      category: "field-trip",
      image: "/images/events/jica-field-trip.jpg",
      icon: <FaMapMarkedAlt />,
      attendees: 35,
      past: true,
      videoUrl: "https://youtu.be/bFcyVygIyWE",
      leaders: [
        {
          name: "Alhassan Kuyateh",
          title: "Co-Founder, Leotech SL"
        },
        {
          name: "Isaiah Lebbie",
          title: "Engineer"
        }
      ]
    },
    {
      id: "giftmed-field-trip-2023",
      title: "Field Trip to GiftMed Construction Site",
      description: "Leotech Building Information Modelling Trainees take a Field trip to the GiftMed Company's construction site in Eastern Freetown.",
      date: "2023-08-12T09:00:00",
      location: "Eastern Freetown",
      category: "field-trip",
      image: "/images/events/giftmed-field-trip.jpg",
      icon: <FaMapMarkedAlt />,
      attendees: 25,
      past: true,
      leaders: [
        {
          name: "Mohamed Sylvanus Kamar",
          title: "Civil Engineer, Leotech"
        }
      ]
    },
    {
      id: "secon-field-trip-2023",
      title: "Field Trip to SECON Foot Bridge Construction Site",
      description: "Visit to SECON Sierra Leone Limited's Construction Site for the Foot Bridge at Lumley.",
      date: "2023-04-22T09:00:00",
      location: "Lumley, Freetown",
      category: "field-trip",
      image: "/images/events/secon-field-trip.jpg",
      icon: <FaMapMarkedAlt />,
      attendees: 30,
      past: true
    },
    {
      id: "entrepreneurship-fair-2023",
      title: "National Youth Employment and Entrepreneurship Fair 2023",
      description: "Leotech participated in the National Youth Employment and Entrepreneurship Fair, showcasing training programs and success stories.",
      date: "2023-08-15T09:00:00",
      location: "Bintumani Conference Center, Freetown",
      category: "exhibition",
      image: "/images/events/entrepreneurship-fair.jpg",
      icon: <FaIndustry />,
      attendees: 500,
      past: true
    },
    {
      id: "annual-meeting-2023",
      title: "Annual General Meeting 2023",
      description: "Leotech hosts an annual meeting to take stock of accomplishments and strategize for the following year.",
      date: "2023-12-15T15:00:00",
      location: "Brookfields, Freetown",
      category: "meeting",
      image: "/images/events/annual-meeting-2023.jpg",
      icon: <FaUsers />,
      attendees: 25,
      past: true
    },
    {
      id: "cohort-5-certification-2022",
      title: "Cohort 5 Certification & Career Talk",
      description: "Certification ceremony and career talk for Cohort 5 on the theme 'Skills Against Poverty'.",
      date: "2022-10-18T14:00:00",
      location: "Mining Engineering Auditorium, Fourah Bay College",
      category: "certification",
      image: "/images/events/cohort-5-cert.jpg",
      icon: <FaCertificate />,
      attendees: 100,
      past: true,
      theme: "Skills Against Poverty"
    },
    {
      id: "secon-field-trip-2022",
      title: "Field Trip to SECON Construction Site",
      description: "Visit to SECON Sierra Leone Limited's Construction Site.",
      date: "2022-09-15T09:00:00",
      location: "Leister, Sierra Leone",
      category: "field-trip",
      image: "/images/events/secon-field-trip-2022.jpg",
      icon: <FaMapMarkedAlt />,
      attendees: 25,
      past: true
    },
    {
      id: "workshop-mentorship-2022",
      title: "Workshop and Career Mentorship",
      description: "Workshop and career mentorship event for youths.",
      date: "2022-06-15T10:00:00",
      location: "Kingtom, Freetown",
      category: "workshop",
      image: "/images/events/workshop-mentorship.jpg",
      icon: <FaChalkboardTeacher />,
      attendees: 45,
      past: true
    },
    {
      id: "cohort-3-4-cert-2021",
      title: "Cohort 3 & 4 Certification & Career Talk",
      description: "Certification and career talk ceremony featuring diaspora investors and entrepreneurs.",
      date: "2021-11-20T14:00:00",
      location: "Prince of Wales School, Kingtom",
      category: "certification",
      image: "/images/events/cohort-3-4-cert.jpg",
      icon: <FaCertificate />,
      attendees: 120,
      past: true,
      speakers: [
        {
          name: "Mr. Joshua Kamanda",
          title: "Sierra Leone Diaspora Investor and Entrepreneur"
        },
        {
          name: "Mr. Sahr Lebbie",
          title: "Sierra Leone Diaspora Investor and Entrepreneur"
        }
      ],
      theme: "Building a Youthful and Self-sufficient Workforce in STEM"
    },
    {
      id: "cohort-2-cert-2021",
      title: "Cohort 2 Certification & Career Talk",
      description: "Certification ceremony and career talk featuring academic and industry experts.",
      date: "2021-05-18T14:00:00",
      location: "Prince of Wales School, Kingtom",
      category: "certification",
      image: "/images/events/cohort-2-cert.jpg",
      icon: <FaCertificate />,
      attendees: 80,
      past: true,
      speakers: [
        {
          name: "Prof. Kelleh Gbawuru Mansaray",
          title: "Dean of Engineering, Fourah Bay College"
        },
        {
          name: "Dr. Samba Sesay",
          title: "Head of Electrical and Electronics Engineering Department, Fourah Bay College"
        },
        {
          name: "Ing. Bashiru Bundka",
          title: "Private Engineering Consultant"
        },
        {
          name: "Mr. Alieu Bangura",
          title: "Private Engineering Consultant"
        }
      ],
      theme: "A Youth-Centred Approach to Build Capacity in STEM"
    },
    {
      id: "cohort-1-cert-2021",
      title: "Cohort 1 Certification Ceremony",
      description: "Leotech's first certification ceremony where the inaugural class of 24 trainees were trained and certified.",
      date: "2021-01-20T14:00:00",
      location: "Freetown",
      category: "certification",
      image: "/images/events/cohort-1-cert.jpg",
      icon: <FaCertificate />,
      attendees: 60,
      past: true,
      speakers: [
        {
          name: "Guest Speakers",
          title: "Fourah Bay College Engineering Faculty"
        }
      ]
    },
    {
      id: "cohort-5-orientation",
      title: "Cohort 5 Orientation",
      description: "Orientation event for Cohort 5 trainees at Leotech headquarters.",
      date: "2022-07-15T10:00:00",
      location: "Kingtom, Freetown",
      category: "orientation",
      image: "/images/events/cohort-5-orientation.jpg",
      icon: <FaGraduationCap />,
      attendees: 60,
      past: true
    }
  ],
  
  // Event categories for filtering
  categories: [
    { id: 'all', name: 'All Events' },
    { id: 'certification', name: 'Certification Ceremonies', icon: <FaCertificate /> },
    { id: 'conference', name: 'Conferences', icon: <FaUsers /> },
    { id: 'orientation', name: 'Orientations', icon: <FaGraduationCap /> },
    { id: 'workshop', name: 'Workshops', icon: <FaChalkboardTeacher /> },
    { id: 'field-trip', name: 'Field Trips', icon: <FaMapMarkedAlt /> },
    { id: 'lecture', name: 'Public Lectures', icon: <FaChalkboardTeacher /> },
    { id: 'project', name: 'Projects', icon: <FaProjectDiagram /> },
    { id: 'panel-discussion', name: 'Panel Discussions', icon: <FaComments /> },
    { id: 'exhibition', name: 'Exhibitions', icon: <FaIndustry /> },
    { id: 'community-service', name: 'Community Service', icon: <FaHandsHelping /> },
    { id: 'meeting', name: 'Meetings', icon: <FaUsers /> }
  ]
};

// Helper functions
export const getAllEvents = () => {
  return [
    EVENTS_DATA.featured,
    ...EVENTS_DATA.upcoming,
    ...EVENTS_DATA.past
  ];
};

export const getUpcomingEvents = () => {
  return [
    EVENTS_DATA.featured,
    ...EVENTS_DATA.upcoming
  ];
};

export const getPastEvents = () => {
  return EVENTS_DATA.past;
};

export const getFeaturedEvents = () => {
  return [
    EVENTS_DATA.featured,
    ...EVENTS_DATA.upcoming.filter(event => event.featured)
  ];
};

export const getEventsByCategory = (category) => {
  if (category === 'all') {
    return getAllEvents();
  }
  
  return getAllEvents().filter(event => event.category === category);
};

export const getEventById = (id) => {
  return getAllEvents().find(event => event.id === id);
};

export default EVENTS_DATA;